import { ChangeDetectorRef, Component, inject, OnInit, ViewEncapsulation } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { ENV, LoggerService } from '@danangelus/ngx-app-core'
import { ConfirmationService } from 'primeng/api'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { DatePicker } from 'primeng/datepicker'
import { DividerModule } from 'primeng/divider'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputTextModule } from 'primeng/inputtext'
import { KeyFilterModule } from 'primeng/keyfilter'
import { MessageModule } from 'primeng/message'
import { PickListModule } from 'primeng/picklist'
import { SelectModule } from 'primeng/select'
import { TableModule } from 'primeng/table'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { SessionService } from '../../../../core/security/session.service'
import { AppManagerService } from '../../../../core/services/app-manager.service'
import { LdapManagerService } from '../../../../core/services/ldap-manager.service'
import { ValidationUtils } from '../../../../core/utils/validation.utils'
import { GatewayError } from '../../../../models/gateway.model'
import { Group } from '../../../../models/group.model'
import { User } from '../../../../models/user.model'

const log = LoggerService.get('EditUserComponent')

/**
 * Component responsible for the management of a single user:
 * <ul>
 *     <li>Creating</li>
 *     <li>Modifying</li>
 *     <li>Deleting</li>
 * </ul>
 *
 * This component is designed to be accessible by either DCS Admins or Dealer Admins only
 *
 * @author Hayden Eastwell
 * @author Dan Bennett
 */
@Component({
    selector: 'edit-user',
    standalone: true,
    imports: [
        // Angular
        FormsModule,
        ReactiveFormsModule,
        // PrimeNG
        DatePicker,
        ConfirmDialogModule,
        DividerModule,
        InputTextModule,
        FloatLabelModule,
        KeyFilterModule,
        MessageModule,
        PickListModule,
        SelectModule,
        ToggleSwitchModule,
        TableModule,
    ],
    providers: [ConfirmationService],
    templateUrl: './edit-user.component.html',
    styleUrl: './edit-user.component.sass',
    encapsulation: ViewEncapsulation.None
})
export class EditUserComponent implements OnInit {

    route = inject(ActivatedRoute)
    router = inject(Router)
    appManager = inject(AppManagerService)
    cdr = inject(ChangeDetectorRef)
    confirmationService = inject(ConfirmationService)
    ldapService = inject(LdapManagerService)
    sessionService = inject(SessionService)

    languages = ['en_GB', 'fr_FR', 'de_DE', 'it_IT']
    editing = false
    // @ts-expect-error Assigned in ngOnInit
    form: UntypedFormGroup

    passwordConfirmation = ''

    id: string | undefined
    subId: string | undefined
    user: User | undefined

    // Table data
    applications: any[] = []
    selected: any[] = []

    groups: Group[] = []
    availableGroups: Group[] = []
    assignedGroups: Group[] = []
    passwordLastSetDate: Date | undefined
    accountExpirationDate: Date | undefined

    messageType = ''

    async ngOnInit() {
        this.route.params.subscribe(params => {
            this.id = params['userId']
        })
        if (this.id) {
            this.editing = true
            const response = await this.ldapService.getUser(this.id!)
            if (response instanceof GatewayError) {
                log.error(`Error with code: ${response.error} and message: ${response.message} when retrieving user ${this.id}`)
                await this.cancel()
            } else {
                this.user = response
            }
            if (this.isDealerAdmin) {
                // TODO: Verify if `subId` is used in the code or if it can be removed at all
                this.subId = this.user!.userId?.substring(this.sessionService.user()!.getId()!.length)
            }
            if (this.user?.passwordLastSet) {
                this.passwordLastSetDate = this.convertToDate(this.user!.passwordLastSet)
            }
            if (this.user?.accountExpiration) {
                this.accountExpirationDate = this.convertToDate(this.user!.accountExpiration)
            }

        } else {
            if (this.isDealerAdmin) {
                const response = await this.ldapService.getUser((this.sessionService.user()!.getId()!))
                if (response instanceof GatewayError) {
                    log.error(`Error with code: ${response.error} and message: ${response.message} when retrieving user ${this.id}`)
                    await this.cancel()
                } else {
                    const user = response
                    this.user = new User({
                        userId: user!.userId,
                        subId: await this.ldapService.getSubIdSuggestion(),
                        dealerNo: user!.dealerNo,
                        dealerName: user!.dealerName,
                        language: user!.language,
                        distributorCode: user!.distributorCode,
                        groups: user!.groups.filter((group: string) => group.toUpperCase() != 'DEALERADMIN' && group.toUpperCase() != 'DCSADMINISTRATOR')
                    })
                }
                this.id = this.user!.userId
            } else {
                this.user = new User()
            }
        }

        let controls
        if (this.isDealerAdmin) {
            controls = {
                subId: new UntypedFormControl(this.subId || this.user?.subId, [Validators.required, Validators.pattern(ValidationUtils.SUB_ID_PATTERN)])
            }
        }

        this.form = new UntypedFormGroup({
            ...controls,
            userId: new UntypedFormControl(this.user?.userId, [Validators.required, Validators.pattern(ValidationUtils.ID_PATTERN)]),
            name: new UntypedFormControl(this.user?.name, [Validators.required,]),
            phone: new UntypedFormControl(this.user?.phone, [Validators.pattern(ValidationUtils.PHONE_PATTERN)]),
            email: new UntypedFormControl(this.user?.email, [Validators.required, Validators.pattern(ValidationUtils.EMAIL_PATTERN)]),
            password: new UntypedFormControl(this.user?.password, [Validators.pattern(ValidationUtils.PASSWORD_PATTERN)]),
            confirmPassword: new UntypedFormControl(this.passwordConfirmation, []),
            language: new UntypedFormControl(this.user?.language, []),
            distributorCode: new UntypedFormControl(this.user?.distributorCode, [Validators.maxLength(50)]),
            dealerNo: new UntypedFormControl(this.user?.dealerNo, [Validators.pattern(ValidationUtils.DEALER_NO_PATTERN)]),
            dealerName: new UntypedFormControl(this.user?.dealerName, []),
            postcode: new UntypedFormControl(this.user?.postcode, []),
            city: new UntypedFormControl(this.user?.city, []),
            street: new UntypedFormControl(this.user?.street, []),
            stationCode: new UntypedFormControl(this.user?.stationCode, [Validators.maxLength(50)]),
            locked: new UntypedFormControl(this.user?.locked, Validators.required),
            resetMfa: new UntypedFormControl(false, []),
            groups: new UntypedFormControl(this.user?.groups, []),
            accountExpiration: new UntypedFormControl(this.accountExpirationDate, []),
            passwordLastSet: new UntypedFormControl(this.passwordLastSetDate, []),
        })

        this.groups = await this.ldapService.getAllGroups()

        if (this.id) {
            this.assignedGroups = this.groups.filter(group => this.user?.groups?.includes(group.groupId!))
            this.form.get('userId')?.disable()
        } else {
            const defaultGroups = ENV.config.get('default-groups')
            this.assignedGroups = this.groups.filter(group => defaultGroups.includes(group.groupId))
        }
        this.form.get('passwordLastSet')?.disable()

        if (this.isDealerAdmin) {
            this.applications = await this.appManager.getAvailableApplicationsForSubUser()

            if (this.id) {
                const assignedGroupNames = this.assignedGroups.map((group) => group.groupId)
                this.selected = this.applications.filter((app: any) => {
                    let match = true
                    const groupNames = app.groups.split(',')
                    groupNames.forEach((groupName: string) => {
                        if (!assignedGroupNames.includes(groupName)) {
                            match = false
                        }
                    })
                    return match
                })
            }

        } else {
            this.availableGroups = this.groups.filter(group => !this.assignedGroups.includes(group))
            this.sortGroups(this.availableGroups)
            this.sortGroups(this.assignedGroups)
        }
    }

    get isDealerAdmin(): boolean {
        return this.sessionService.user()!.groups?.includes('DEALERADMIN')
    }

    private additionalGroups(): string[] {
        const additionalGroups: string[] = []
        const includedGroups = ENV.config.get('groups-to-include-for-managed-users') as string[]
        const dealerAdminGroups = this.sessionService.user()?.groups
        dealerAdminGroups?.forEach((group: string) => {
            if (includedGroups.find(g => g.toUpperCase() == group.toUpperCase())) {
                additionalGroups.push(group)
            }
        })
        return additionalGroups
    }

    /**
     * A handler for the submit button's click event.
     *
     * This will send a PUT/POST request via {@linkcode LdapManagerService#saveUser}
     * to conditionally create/update the User record with the form's values.
     */
    async save() {
        log.debug('save', `New User data submitted: ${this.user}`)

        const newPass = this.form.controls['password']
        const confirmPass = this.form.controls['confirmPassword']
        if ((newPass.value?.length > 0 || confirmPass.value?.length > 0) && newPass.value !== confirmPass.value) {
            newPass.setErrors({ noMatch: true })
            confirmPass.setErrors({ noMatch: true })
            this.messageType = 'PASSWORD_NO_MATCH'
            return
        } else {
            newPass.setErrors(null)
            confirmPass.setErrors(null)
            newPass.updateValueAndValidity()
        }

        await this.validateAll()
        if (this.form.invalid) {
            if (newPass.errors && newPass.errors['pattern']) {
                this.messageType = 'PASSWORD_INVALID'
            } else {
                this.messageType = 'FORM_INVALID'
            }
            return
        }

        try {
            const normalizedValues = this.emptyStringsToNull(this.form.value)
            this.user = new User(normalizedValues)

            // set account expiration back to epoch
            this.user.accountExpiration = this.convertToEpoch(this.form.get('accountExpiration')?.value)

            let response: any
            if (!this.isDealerAdmin) {
                this.user.groups = this.assignedGroups.map(group => group.groupId!)
                if (this.id) {
                    this.user.userId = this.id
                    const resetMfa = this.form.get('resetMfa')?.value
                    response = await this.ldapService.saveUser(this.user!, resetMfa)
                } else {
                    response = await this.ldapService.createUser(this.user!)
                }
            } else {
                const groups: string[] = ['User']
                this.selected.forEach((app: any) => {
                    const groupNames: string[] = app['groups'].split(',')
                    groupNames.forEach((group) => {
                        if (!groups.includes(group)) {
                            groups.push(group)
                        }
                    })
                })
                this.user.groups = groups
                this.user.groups.push(...this.additionalGroups())
                if (this.editing) {
                    this.user.userId = this.id
                    response = await this.ldapService.saveUser(this.user!)
                } else {
                    this.user.userId = `${this.id}${this.form.get('subId')!.value}`
                    response = await this.ldapService.createUser(this.user!)
                }
            }

            if (response instanceof GatewayError) {
                switch (response.error) {
                    case 'NOT_UNIQUE':
                        this.form.controls[this.isDealerAdmin ? 'subId' : 'userId'].setErrors({ duplicate: true })
                        this.messageType = response.error
                        break
                    case 'NOT_EXISTS':
                        this.messageType = response.error
                        break
                    case 'PASSWORD_INSECURE':
                        this.messageType = response.error
                        break
                    case 'MISSING_PASSWORD':
                        this.messageType = response.error
                        break
                    default:
                        this.messageType = 'ERROR_SAVING'
                }
            } else {
                log.debug('save', `User [${this.user!.userId}] has been updated`)
                this.messageType = 'CHANGES_SAVED'
                await this.cancel()
            }
        } catch (err: any) {
            log.error(`Error when ${this.editing ? 'updating' : 'creating'} User ${this.user!.userId} ${err}`)
            this.messageType = 'ERROR_SAVING'
        }
    }

    /**
     * A handler for the delete button's click event.
     *
     * This will send a delete request via {@linkcode LdapManagerService#deleteUser}
     * to remove the current User from the LDAP server.
     */
    async delete(event: Event) {
        log.debug('delete', `Deleting User ${this.user!.userId}`)
        if (this.id === this.sessionService.user()?.userId) {
            this.messageType = 'CANNOT_DELETE_SELF'
            return
        }
        event.preventDefault()
        this.confirmationService.confirm({
            message: `Are you sure you want to delete User ${this.user!.userId}`,
            header: `Delete ${this.user!.userId}?`,
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                log.debug('delete', `Deleting User ${this.user!.userId}`)
                await this.ldapService.deleteUser(this.user!.userId!, this.sessionService.user()?.userId ?? null)
                await this.cancel()
            },
            reject: () => {
                this.messageType = 'DELETION_CANCELLED'
            }
        })
    }

    /**
     * Handler for the cancel button's click event.
     *
     * This will cancel the create/update and redirect the User back to the list view
     */
    async cancel(): Promise<void> {
        await this.router.navigateByUrl(`/administration/${this.isDealerAdmin ? 'sub-user' : 'user'}`, { replaceUrl: true })
    }

    /**
     * Sort the assigned/available groups in the picklist in alphabetical order.
     *
     * Uses setTimeout to defer the sort until after PrimeNG's PickList has
     * finished its internal array mutations and change detection cycle.
     *
     * @param groups List of assigned/available groups
     */
    sortGroups(groups: Group[]): void {
        groups.sort((group1, group2) => {
            const sortOn1 = group1.displayName ?? group1.groupId ?? ''
            const sortOn2 = group2.displayName ?? group2.groupId ?? ''
            return sortOn1.localeCompare(sortOn2)
        })
    }

    /**
     * Mark each FormControl as dirty and re-evaluate validity to establish
     * validation failures
     */
    async validateAll() {
        Object.values(this.form.controls).forEach((control: any) => {
            control.markAsDirty()
        })
        this.form.updateValueAndValidity()
    }

    // Used to convert an epoch format to a Date object
    convertToDate(date: number | undefined): Date | undefined {
        if (!isNaN(date!)) {
            return new Date(date!)
        } else {
            return undefined
        }
    }

    // Used to convert a Date object back to epoch format
    convertToEpoch(date: Date | undefined): number | undefined {
        if (date) {
            return date.getTime()
        } else {
            return undefined
        }
    }

    emptyStringsToNull<T extends object>(obj: T): T {
        const result: any = {};

        for (const [key, value] of Object.entries(obj)) {
            const trimmedValue = (typeof value === 'string') ? value.trim() : value;
            result[key] = (trimmedValue === '') ? null : trimmedValue;
        }

        return result;
    }


    protected readonly ValidationUtils = ValidationUtils
}
